import React, { useState, useEffect, useContext } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Heading,
  Grid,
  Card,
  CardBody,
  Flex,
  Text,
  Button,
  Avatar,
  Skeleton,
  Stack,
  Icon,
  Alert,
  AlertIcon,
  useColorModeValue,
} from '@chakra-ui/react';
import { ChatIcon } from '@chakra-ui/icons';
import AuthContext from '../context/AuthContext';
import { sessionService } from '../services/api';

function MySessions() {
  const { token } = useContext(AuthContext);
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  const cardBg = useColorModeValue('white', 'gray.700');
  const cardBorder = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.600', 'gray.300');

  useEffect(() => {
    fetchSessions();
  }, [token]);

  const fetchSessions = async () => {
    try {
      setIsLoading(true);
      const data = await sessionService.getAllSessions();
      // Sort sessions by most recent activity
      const sortedSessions = data.sort((a, b) =>
        new Date(b.updatedAt) - new Date(a.updatedAt)
      );
      setSessions(sortedSessions);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getLastMessage = (messages) => {
    if (!messages || messages.length === 0) return 'No messages yet';
    const lastMessage = messages[messages.length - 1];
    const preview = lastMessage.content.substring(0, 100);
    return preview + (lastMessage.content.length > 100 ? '...' : '');
  };

  const renderSkeletons = () => {
    return Array(6).fill(0).map((_, i) => (
      <Card key={i} bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" overflow="hidden" boxShadow="md">
        <CardBody>
          <Flex alignItems="center" mb={4}>
            <Skeleton height="60px" width="60px" borderRadius="full" mr={4} />
            <Stack flex={1} spacing={2}>
              <Skeleton height="20px" width="60%" />
              <Skeleton height="16px" width="40%" />
            </Stack>
          </Flex>
          <Stack spacing={2} mb={4}>
            <Skeleton height="14px" />
            <Skeleton height="14px" />
            <Skeleton height="14px" width="80%" />
          </Stack>
          <Skeleton height="40px" width="100%" borderRadius="md" />
        </CardBody>
      </Card>
    ));
  };

  return (
    <Container maxW="container.xl" py={8}>
      <Heading as="h1" size="xl" mb={8} color="brand.600">
        My Sessions
      </Heading>

      {isLoading ? (
        <Grid
          templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }}
          gap={6}
        >
          {renderSkeletons()}
        </Grid>
      ) : sessions.length === 0 ? (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Box>
            <Text fontWeight="bold">No sessions yet</Text>
            <Text>Start a conversation with a character to see your sessions here.</Text>
          </Box>
        </Alert>
      ) : (
        <Grid
          templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }}
          gap={6}
        >
          {sessions.map((session) => (
            <Card 
              key={session._id} 
              bg={cardBg}
              borderWidth="1px" 
              borderColor={cardBorder}
              borderRadius="lg" 
              overflow="hidden"
              boxShadow="md"
              transition="transform 0.3s, box-shadow 0.3s"
              _hover={{
                transform: "translateY(-5px)",
                boxShadow: "lg"
              }}
            >
              <CardBody>
                <Flex alignItems="center" mb={4}>
                  {session.characterId?.imageData ? (
                    <Avatar
                      size="lg"
                      src={`/api/characters/${session.characterId._id}/image`}
                      name={session.characterId.character_profile?.character_name}
                      mr={4}
                    />
                  ) : (
                    <Avatar
                      size="lg"
                      name={session.characterId?.character_profile?.character_name || 'Unknown'}
                      bg="brand.500"
                      mr={4}
                    />
                  )}
                  <Box flex={1}>
                    <Heading size="md" mb={1}>
                      {session.name}
                    </Heading>
                    <Text fontSize="sm" color={textColor}>
                      {session.characterId?.character_profile?.character_name || 'Unknown Character'}
                    </Text>
                  </Box>
                </Flex>

                <Box mb={4}>
                  <Text fontSize="sm" color={textColor} mb={2}>
                    <strong>Last activity:</strong> {formatDate(session.updatedAt)}
                  </Text>
                  <Box 
                    bg={useColorModeValue('gray.50', 'gray.800')} 
                    p={3} 
                    borderRadius="md"
                    fontSize="sm"
                    color={textColor}
                    height="60px"
                    overflow="hidden"
                  >
                    <Text noOfLines={3}>
                      {getLastMessage(session.messages)}
                    </Text>
                  </Box>
                </Box>

                <Button
                  as={RouterLink}
                  to={`/chat/${session._id}`}
                  colorScheme="brand"
                  width="100%"
                  leftIcon={<Icon as={ChatIcon} />}
                >
                  Continue Chatting
                </Button>
              </CardBody>
            </Card>
          ))}
        </Grid>
      )}
    </Container>
  );
}

export default MySessions;